import { useEffect, useState } from "react";
import { auth } from "./auth";
import type { Track } from "../types";

export const DAILY_LISTEN_LIMIT = 10;

export interface DailyQuota {
  isPremium: boolean;
  limit: number;
  count: number;
  remaining: number;
  userId: string | number;
}

export function getTodayDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function getQuotaUserId(): string | number {
  const user = auth.user();
  if (user?.id) return user.id;
  try {
    const raw = localStorage.getItem("user_status") || localStorage.getItem("user");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id || parsed?.user_id) return parsed.id ?? parsed.user_id;
    }
  } catch {}
  return `guest_${auth.deviceId()}`;
}

export function isUserPremium(): boolean {
  const user = auth.user() as any;
  if (
    user &&
    (user.is_premium === true ||
      user.is_premium === "true" ||
      user.is_premium === 1 ||
      user.role === "premium" ||
      user.role === "vip" ||
      user.is_subscribed ||
      user.has_subscription ||
      user.has_active_subscription ||
      user.is_vip)
  ) {
    return true;
  }

  const checkKeys = ["user_status", "ym_user", "user", "profile", "user_profile", "auth_user"];
  for (const key of checkKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          (parsed.is_premium === true ||
            parsed.is_premium === "true" ||
            parsed.is_premium === 1 ||
            parsed.role === "premium" ||
            parsed.role === "vip" ||
            parsed.is_subscribed ||
            parsed.has_subscription ||
            parsed.has_active_subscription ||
            parsed.is_vip ||
            parsed.user?.is_premium === true ||
            parsed.user?.is_premium === "true" ||
            parsed.user?.is_premium === 1 ||
            parsed.user?.role === "premium" ||
            parsed.user?.role === "vip" ||
            parsed.user?.is_subscribed ||
            parsed.profile?.is_premium === true ||
            parsed.profile?.is_premium === "true" ||
            parsed.profile?.is_premium === 1 ||
            parsed.profile?.is_subscribed ||
            parsed.profile?.has_active_subscription ||
            parsed.profile?.role === "premium")
        ) {
          return true;
        }
      }
    } catch {}
  }

  try {
    const rawSub = localStorage.getItem("subscriptionStatus") || localStorage.getItem("subscriptions");
    if (rawSub) {
      const parsedSub = JSON.parse(rawSub);
      if (Array.isArray(parsedSub) && parsedSub.some((s: any) => s?.status === "active")) {
        return true;
      }
    }
  } catch {}

  return false;
}

export function getDailyQuota(): DailyQuota {
  const isPremium = isUserPremium();
  const user = auth.user();

  if (isPremium) {
    return {
      isPremium: true,
      limit: Infinity,
      count: 0,
      remaining: Infinity,
      userId: user?.id ?? "premium_user",
    };
  }

  const userId = getQuotaUserId();
  const dateKey = getTodayDateString();
  const storageKey = `daily_listen_count_${userId}_${dateKey}`;
  
  let count = 0;
  try {
    const stored = localStorage.getItem(storageKey);
    count = stored ? Math.max(0, parseInt(stored, 10) || 0) : 0;
  } catch {
    count = 0;
  }

  const remaining = Math.max(0, DAILY_LISTEN_LIMIT - count);

  return {
    isPremium: false,
    limit: DAILY_LISTEN_LIMIT,
    count,
    remaining,
    userId,
  };
}

let lastCountedTrackId: string | number | null = null;
let lastCountedDate: string = "";

export function recordDailyListen(track?: Track | null): { allowed: boolean; remaining: number } {
  const quota = getDailyQuota();

  if (quota.isPremium) {
    return { allowed: true, remaining: Infinity };
  }

  // If track is explicitly marked to ignore quota limits (e.g., Tunes / Audio Library)
  if (track?.ignore_limit) {
    return { allowed: true, remaining: quota.remaining };
  }

  const today = getTodayDateString();
  const trackId = track?.id ?? null;

  // If this track was already recorded in the active session today, allow without double-counting
  if (trackId && lastCountedTrackId === trackId && lastCountedDate === today) {
    return { allowed: true, remaining: quota.remaining };
  }

  if (quota.remaining <= 0) {
    triggerSubscriptionModal();
    return { allowed: false, remaining: 0 };
  }

  const newCount = quota.count + 1;
  const storageKey = `daily_listen_count_${quota.userId}_${today}`;
  try {
    localStorage.setItem(storageKey, String(newCount));
  } catch (e) {
    console.error("Failed to save daily listen count to localStorage:", e);
  }

  lastCountedTrackId = trackId;
  lastCountedDate = today;

  const newRemaining = Math.max(0, DAILY_LISTEN_LIMIT - newCount);

  notifyQuotaChanged();

  return { allowed: true, remaining: newRemaining };
}

export function resetSessionCountedTrack(): void {
  lastCountedTrackId = null;
  lastCountedDate = "";
}

export function triggerSubscriptionModal(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ym_open_subscription_modal"));
  }
}

export function notifyQuotaChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ym_quota_updated"));
  }
}

export function useDailyQuota(): DailyQuota {
  const [quota, setQuota] = useState<DailyQuota>(() => getDailyQuota());

  useEffect(() => {
    const update = () => {
      setQuota(getDailyQuota());
    };

    update();

    window.addEventListener("ym_quota_updated", update);
    window.addEventListener("storage", update);

    return () => {
      window.removeEventListener("ym_quota_updated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  return quota;
}
